import React from 'react';
import { Box, Card, CardContent, Typography, Button, Divider } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckIcon from '@mui/icons-material/Check';
import axios from 'axios';
import config from 'config';
import { getAuth } from 'firebase/auth';
// import { alpha } from '@mui/material/styles';

const InsufficientCreditsFreeCard = ({ meta, toolDisplayName }) => {
  const available = Number(meta?.available_credits || 0);
  const required = Number(meta?.required_credits || 0);
  const missing = Math.max(0, required - available);
  const tname = toolDisplayName(String(meta?.tool_name || '').trim() || 'Requested analysis');
  const priceInfo = { price: '79.00/mo', unsubscribe: 'Unsubscribe anytime.' };
  const benefits = [
    'Export to CSV, MS Word',
    'History access',
    'Extended reports: Audience Segmentation, SWOT, Market Overview',
    'Competition analysis',
    'Reviews Analysis'
  ];

  const [upgradeLoading, setUpgradeLoading] = React.useState(false);
  const euCountryCodes = React.useMemo(
    () => [
      'AT',
      'BE',
      'BG',
      'HR',
      'CY',
      'CZ',
      'DK',
      'EE',
      'FI',
      'FR',
      'DE',
      'GR',
      'HU',
      'IE',
      'IT',
      'LV',
      'LT',
      'LU',
      'MT',
      'NL',
      'PL',
      'PT',
      'RO',
      'SK',
      'SI',
      'ES',
      'SE'
    ],
    []
  );
  const [isEUDisplay, setIsEUDisplay] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch('https://ipinfo.io/json?token=4f1c89b6de8103');
        const data = await resp.json();
        const code = data && data.country;
        const eu = !!(code && euCountryCodes.includes(code));
        if (!cancelled) setIsEUDisplay(eu);
      } catch (_) {
        if (!cancelled) setIsEUDisplay(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [euCountryCodes]);

  const startUpgradeCheckout = React.useCallback(async () => {
    if (upgradeLoading) return;
    try {
      setUpgradeLoading(true);
      try {
        if (window.gtag) {
          window.gtag('event', 'conversion', { send_to: 'AW-16481265161/uSXlCMCs_9YZEInM8LI9', value: 39.0, currency: 'EUR' });
        }
        if (window.ym) {
          window.ym(94496474, 'reachGoal', 'START_TRIAL_CLiCK');
        }
      } catch (_) {
        /* ignore analytics errors */ void 0;
      }
      let isEU = false;
      try {
        const resp = await fetch('https://ipinfo.io/json?token=4f1c89b6de8103');
        const data = await resp.json();
        const code = data && data.country;
        isEU = !!(code && euCountryCodes.includes(code));
      } catch (_) {
        isEU = false;
      }
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const priceId = isEU ? config.priceIdProEUMonth : config.priceIdProUSMonth;
      const url = `${config.apiUrl}/pro_plans/create_checkout_session/${priceId}?trial_days=false`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      const target = res?.data?.url;
      if (target) window.location.href = target;
    } catch (_) {
      // silent
    } finally {
      setUpgradeLoading(false);
    }
  }, [upgradeLoading, euCountryCodes]);

  const percent = React.useMemo(() => {
    if (!required || required <= 0) return 0;
    const p = (available / required) * 100;
    if (!isFinite(p) || isNaN(p)) return 0;
    return Math.max(0, Math.min(100, p));
  }, [available, required]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, width: { xs: '100%', sm: '55%' } }}>
      <Card
        sx={{
          maxWidth: '100%',
          width: '100%',
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.paper} 40%, ${theme.palette.primary.main}12)`,
          color: (theme) => theme.palette.text.primary,
          borderRadius: '20px 20px 20px 4px',
          boxShadow: 'none',
          border: (theme) => `1px solid ${theme.palette.divider}`
        }}
      >
        <CardContent sx={{ px: 3, py: 3 }}>
          <Box sx={{ width: { xs: '100%', sm: '100%' }, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: 9999,
                  bgcolor: (theme) => theme.palette.action.hover,
                  color: (theme) => theme.palette.text.secondary,
                  fontSize: 12,
                  mb: 1
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                Almost There
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                You&apos;re close to the insights you need
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Just {missing} more credits to unlock the complete {tname.toLowerCase()} you&apos;re looking for.
              </Typography>
            </Box>

            {/* Credit Visualization */}
            <Card sx={{ p: 2, borderRadius: 4, boxShadow: 'none', border: (theme) => `1px solid ${theme.palette.divider}`, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="caption" color="text.secondary">
                    You have
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {available}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">
                    You need
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {required}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <Box
                  sx={{
                    height: 8,
                    width: '100%',
                    bgcolor: (theme) => theme.palette.action.selected,
                    borderRadius: 9999,
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${percent}%`,
                      bgcolor: 'transparent',
                      background: 'linear-gradient(90deg, #3b82f6, #6366f1, #8b5cf6)',
                      transition: 'width 300ms ease'
                    }}
                  />
                </Box>
                <Typography align="center" variant="caption" sx={{ mt: 0.75, display: 'block' }}>
                  <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {missing} more credits
                  </Typography>{' '}
                  to continue
                </Typography>
              </Box>
            </Card>

            {/* Pro credits highlight */}
            <Card
              sx={{
                p: 2,
                mb: 2,
                bgcolor: (theme) => `${theme.palette.primary.main}19`,
                border: (theme) => `1px solid ${theme.palette.primary.main}33`,
                borderRadius: 4,
                boxShadow: 'none'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    With Pro you get
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      300
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      credits/month
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    height: 40,
                    width: 40,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <BoltIcon sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
              </Box>
            </Card>

            {/* Features */}
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Plus all Pro features:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {benefits.map((feature) => (
                  <Box key={feature} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                      sx={{
                        mt: '2px',
                        height: 20,
                        width: 20,
                        borderRadius: '50%',
                        bgcolor: (theme) => `${theme.palette.primary.main}22`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <CheckIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                    </Box>
                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* CTA */}
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                size="medium"
                fullWidth
                disabled={upgradeLoading}
                onClick={startUpgradeCheckout}
                sx={{
                  background: 'linear-gradient(90deg, #3b82f6 0%, #34d399 100%)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #2563eb 0%, #10b981 100%)'
                  },
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontSize: '0.95rem'
                }}
              >
                {upgradeLoading ? 'Redirecting…' : `Upgrade Now — ${isEUDisplay ? '€' : '$'}${priceInfo.price}`}
              </Button>
              <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 0.75 }}>
                {priceInfo.unsubscribe}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InsufficientCreditsFreeCard;
